import { Container, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import i18next from "i18next";
import { DateTime, Duration } from "luxon";
import React from "react";

import { DateOnly } from "../../types/date-time-types";
import { ofDateOnly } from "../../utils/time-utils";

const useStyles = makeStyles((theme) => ({
  container: {
    "& .calendar-section": {
      width: "100%",
      marginBottom: 30,
      [theme.breakpoints.up("md")]: {
        marginBottom: "auto",
      },
      "& .weekday-strip": {
        justifyContent: "space-around",
        borderBottom: "1px solid #D8D8D8",
        paddingBottom: 10,
      },
      "& .weekdate-strip": {
        justifyContent: "space-around",
        marginLeft: 0,
        [theme.breakpoints.up("md")]: {
          marginLeft: 12,
        },
      },
      "& .MuiTypography-body1": {
        fontSize: 14,
        height: 19,
        width: 32,
        padding: 15,
        margin: "5px 0 5px -25px",
        textTransform: "uppercase",
        color: "#9B9B9B",
        [theme.breakpoints.up("md")]: {
          margin: 12,
        },
      },
      "& .MuiTypography-body2": {
        fontSize: 18,
        fontWeight: 600,
        marginTop: 8,
        height: 32,
        width: 32,
        padding: "5px",
        textAlign: "center",
        cursor: "pointer",
        borderRadius: "100%",
        color: "#595959",
        [theme.breakpoints.up("md")]: {
          height: 48,
          width: 48,
          padding: "12px",
          textAlign: "center",
        },
        "&.readOnly": {
          cursor: "not-allowed",
          opacity: 0.3,
        },
        "&.active": {
          color: "#ffffff",
          backgroundColor: "#333333",
          opacity: 1,
        },
        "&.current": {
          color: "#ffffff",
          backgroundColor: "#a9a6a6",
        },
      },
    },
  },
}));

type WeekCalendarProps = {
  children?: any;
  isReadOnly?: boolean;
  onChange: (d: DateOnly) => void;
};
const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(
  (d) => i18next.t(`${d.substr(0, 3).toUpperCase()}`)
);

export function createWeekDays(currentDay: DateTime): DateTime[] {
  const week: DateTime[] = [];
  for (let i = 1; i <= 7; i++) {
    const dayOrdinal = currentDay.weekday;
    const day = currentDay.plus(Duration.fromObject({ days: i - dayOrdinal }));
    week.push(day);
  }
  return week;
}

export default function WeekCalendar({ onChange, isReadOnly = false }: WeekCalendarProps) {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = React.useState<DateTime>(DateTime.now());
  const week: DateTime[] = createWeekDays(DateTime.now());
  const currentDay = DateTime.now().day;
  const dateSel = selectedDate.day;

  const onClick = (mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const dataset = mouseEvent.currentTarget.dataset;
    if (dataset.day === undefined) {
      throw new Error("dataset.day should have contained an iso date");
    }
    const newDate = ofDateOnly(dataset.day);
    setSelectedDate(DateTime.fromISO(newDate));
    onChange(newDate);
  };

  return (
    <Container className={classes.container}>
      <Grid container direction="column" className="calendar-section">
        <Grid container item direction="row" className="weekday-strip">
          {weekdays.map((weekDay, i) => (
            <Grid item key={i}>
              <Typography variant="body1">{weekDay}</Typography>
            </Grid>
          ))}
        </Grid>
        <Grid container item direction="row" className="weekdate-strip">
          {week.map((weekDay, i) => {
            const dayOfTheWeek = weekDay.day;
            const isActive = dayOfTheWeek === dateSel;
            const isClickable = !isActive && !isReadOnly;
            const className = clsx({
              current: dayOfTheWeek !== dateSel && dayOfTheWeek === currentDay,
              active: isActive,
              readOnly: !isClickable,
            });

            return (
              <Grid
                item
                key={i}
                data-day={weekDay.toISODate()}
                onClick={isClickable ? onClick : undefined}
              >
                <Typography variant="body2" className={className}>
                  {dayOfTheWeek}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Container>
  );
}
